import {useLocation, useParams, useSearchParams} from "react-router-dom";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {EditLocalStoragePagesData} from "../../../utils/EditLocalStoragePagesData";
import {constantsErrorMessages} from "../../../Constants";
import {Box} from "@mui/system";
import {BreadcrumbsComponent} from "../../../components/UI/breadcrumb/BreadcrumbComponent";
import ModalAlert from "../../../components/UI/table/customer-detales/ModalAlert";
import {Button, Typography} from "@mui/material";
import {ButtonGroupTabBar} from "../../../components/UI/button/ButtonGroupTabBar";
import CheckIcon from "@mui/icons-material/Check";
import TextFieldValidation from "../../../components/UI/table/customer-detales/TextFieldValidation";
import ButtonAdd from "../../../components/UI/button/ButtonAdd";
import VariablesTable from "../../../components/UI/table/customer-detales/processing/VariablesTable";
import {withFilter} from "../../../data/FilterTables/withFilterHOC";
import {VariablesFilterData} from "../../../data/FilterTables/VariablesFilterData";
import CircularProgress from "@mui/material/CircularProgress";
import PacedDetailModulesService from "../../../API/organization/PacedDetailModulesService";
import {
    buildPacedMainFormPayload,
    emptyPointForm,
    pacedDtoToMainForm,
} from "./module3d/pacedDetailModule";
import {normalizeVariablesTableRows} from "../../../utils/compositionVariables";

const PointFields = ({title, point, onPointChange}) => (
    <Box sx={{display: "flex", flexDirection: "column", gap: 0}}>
        <Typography sx={{fontSize: "14px", fontWeight: 600, mt: 1, mb: 0.5}}>{title}</Typography>
        {["x", "y", "z"].map((axis) => (
            <TextFieldValidation
                key={axis}
                name={axis}
                type={"text"}
                value={point?.[axis] ?? "0"}
                label={axis.toUpperCase()}
                setCurrValues={(updater) => {
                    onPointChange((prevPoint) => {
                        const next = typeof updater === "function" ? updater(prevPoint) : updater;
                        return next;
                    });
                }}
                required={false}
            />
        ))}
    </Box>
);

const applyDtoToState = (data, setters) => {
    const {setDto, setForm, setVariables, setTab, savedTab, tableOptionsHandle} = setters;
    setDto(data);
    setForm(pacedDtoToMainForm(data));
    const vars = normalizeVariablesTableRows(data?.variables);
    setVariables(vars);
    setTab(savedTab === 2 ? 2 : 1);
    tableOptionsHandle((prev) => ({
        ...prev,
        totalElements: vars.length,
    }));
};

const PacedDetailModuleItem = () => {
    const ComponentVariablesWithFilter = useMemo(() => {
        return withFilter(VariablesTable, VariablesFilterData);
    }, []);
    const {moduleId, moduleName, pacedDetailId, position} = useParams();
    const [searchParams] = useSearchParams();
    const detailId = searchParams.get("detailId");
    const pathname = useLocation().pathname;
    const positionLabel = position != null ? decodeURIComponent(position) : "";

    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [dto, setDto] = useState(null);
    const [form, setForm] = useState({
        anchor: emptyPointForm(),
        axesX: emptyPointForm(),
        axesY: emptyPointForm(),
        axesZ: emptyPointForm(),
    });
    const [variables, setVariables] = useState([]);
    const [alertData, setAlertData] = useState({});
    const [tab, setTab] = useState(1);
    const refetchTimerRef = useRef(null);
    const [tableOptions, setTableOptions] = useState({
        pageIndex: 0,
        pageSize: 100,
        totalElements: 0,
        searchData: "",
    });

    const tableOptionsHandle = (nextState) => {
        setTableOptions(nextState);
        if (nextState.pageSize !== tableOptions.pageSize) {
            EditLocalStoragePagesData(`${pathname}`, "pageSize", nextState.pageSize);
        }
    };

    const getStartPageSize = () => {
        const pagesData = JSON.parse(localStorage.getItem("pagesData")) || {};
        const savedTab = pagesData[pathname]?.tab || 1;
        return {savedTab};
    };

    const handleCloseAlert = () => {
        setAlertData((alert) => ({...alert, open: false}));
    };

    const handleModalAlert = (message, type = "error") => {
        setAlertData({
            type,
            open: true,
            message: type === "success"
                ? message
                : message.response?.data?.message
                    || constantsErrorMessages[message.response?.status]
                    || message.message,
        });
    };

    useEffect(() => {
        const {savedTab} = getStartPageSize();
        if (pacedDetailId == null || pacedDetailId === "" || pacedDetailId === "new") {
            handleModalAlert(new Error("Нет id размещения детали"));
            setIsLoading(false);
            return;
        }
        PacedDetailModulesService.getById(pacedDetailId)
            .then((response) => {
                applyDtoToState(response.data, {
                    setDto,
                    setForm,
                    setVariables,
                    setTab,
                    savedTab,
                    tableOptionsHandle,
                });
            })
            .catch((error) => {
                handleModalAlert(error);
            })
            .finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pacedDetailId]);

    // Сцена (Module3DView) делает PUT и диспатчит событие — синхронизируем DTO/поля сразу.
    useEffect(() => {
        if (pacedDetailId == null || pacedDetailId === "" || pacedDetailId === "new") return;

        const onPacedDetailUpdated = (e) => {
            const updatedDetailId = e?.detail?.detailId;
            if (String(updatedDetailId) !== String(detailId)) return;

            if (refetchTimerRef.current) {
                window.clearTimeout(refetchTimerRef.current);
            }
            refetchTimerRef.current = window.setTimeout(() => {
                PacedDetailModulesService.getById(pacedDetailId)
                    .then((response) => {
                        applyDtoToState(response.data, {
                            setDto,
                            setForm,
                            setVariables,
                            setTab,
                            savedTab: tab, // placeholder, будет перезаписан applyDtoToState
                            tableOptionsHandle,
                        });
                    })
                    .catch((error) => {
                        handleModalAlert(error);
                    });
            }, 250);
        };

        window.addEventListener("paced-detail-module-updated", onPacedDetailUpdated);
        return () => {
            window.removeEventListener("paced-detail-module-updated", onPacedDetailUpdated);
            if (refetchTimerRef.current) window.clearTimeout(refetchTimerRef.current);
        };
    }, [detailId, pacedDetailId, tab]);

    const handleChangeTab = (nextTab) => {
        setTab(nextTab);
        tableOptionsHandle((prev) => ({
            ...prev,
            totalElements: nextTab === 2 ? variables.length : 0,
        }));
        EditLocalStoragePagesData(pathname, "tab", nextTab);
    };

    const setPoint = (key) => (updater) => {
        setForm((prev) => {
            const current = prev[key] || emptyPointForm();
            const next = typeof updater === "function" ? updater(current) : updater;
            return {...prev, [key]: next};
        });
    };

    const setVariablesFromTable = (next) => {
        const list = typeof next === "function" ? next(variables) : next;
        setVariables(list);
        setDto((prev) => (prev ? {...prev, variables: list} : prev));
    };

    const pacedRecordId = dto?.id != null && Number(dto.id) !== 0 ? dto.id : pacedDetailId;

    const handleUpdate = () => {
        if (detailId == null || detailId === "" || detailId === "new") {
            handleModalAlert(new Error("Нет id детали"));
            return;
        }
        setLoading(true);
        const payload = buildPacedMainFormPayload(form, dto, pacedRecordId);
        PacedDetailModulesService.update(pacedRecordId, detailId, payload)
            .then((response) => {
                const data = response.data;
                setDto(data);
                setForm(pacedDtoToMainForm(data));
                setVariables(normalizeVariablesTableRows(data?.variables));
                handleModalAlert("Данные изменены", "success");
            })
            .catch((error) => {
                handleModalAlert(error);
            })
            .finally(() => setLoading(false));
    };

    const generalTabComponents = [
        {
            tabIndex: 1,
            label: "Основные",
            component: (
                <Box sx={{width: "400px", display: "flex", flexDirection: "column", gap: 0}}>
                    <PointFields
                        title="Точка привязки"
                        point={form.anchor}
                        onPointChange={setPoint("anchor")}
                    />
                    <PointFields
                        title="axesX"
                        point={form.axesX}
                        onPointChange={setPoint("axesX")}
                    />
                    <PointFields
                        title="axesY"
                        point={form.axesY}
                        onPointChange={setPoint("axesY")}
                    />
                    <PointFields
                        title="axesZ"
                        point={form.axesZ}
                        onPointChange={setPoint("axesZ")}
                    />
                    <Box sx={{
                        width: 400,
                        paddingTop: "20px",
                        display: "flex",
                        justifyContent: "flex-end",
                    }}>
                        <ButtonAdd loading={loading} text={"Применить"} onClick={handleUpdate}/>
                    </Box>
                </Box>
            ),
        },
        {
            tabIndex: 2,
            label: "Переменные",
            component: (
                <ComponentVariablesWithFilter
                    data={variables}
                    setData={setVariablesFromTable}
                    enableEditing={true}
                    tableOptions={tableOptions}
                    tableOptionsHandle={tableOptionsHandle}
                    pacedDetailModule
                    pacedDetailModuleId={pacedRecordId}
                />
            ),
        },
    ];

    return (
        <Box sx={{padding: "0 30px"}}>
            <BreadcrumbsComponent
                page={`Размещение ${positionLabel}`}
                path={[
                    {name: "Главная", href: "/"},
                    {name: "Настройки", href: "/settings"},
                    {
                        name: `Модуль ${moduleName}`,
                        href: `/organization/module/${moduleId}`,
                    },
                ]}
            />
            {isLoading ? (
                <Box sx={{
                    height: "100vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}>
                    <CircularProgress disableShrink/>
                </Box>
            ) : (
                <Box sx={{display: "flex", flexDirection: "column", gap: "32px"}}>
                    <Typography sx={{fontSize: "24px", fontWeight: 700}}>
                        {`Размещение ${positionLabel} модуля ${moduleName}`}
                    </Typography>
                    <ButtonGroupTabBar activeTab={tab}>
                        {generalTabComponents.map(({tabIndex, label}) => (
                            <Button
                                key={tabIndex}
                                tabIndex={tabIndex}
                                onClick={() => handleChangeTab(tabIndex)}
                            >
                                <CheckIcon/>
                                {label}
                            </Button>
                        ))}
                    </ButtonGroupTabBar>
                    {generalTabComponents.find((c) => c.tabIndex === tab)?.component}
                </Box>
            )}
            <ModalAlert data={alertData} onClose={handleCloseAlert}/>
        </Box>
    );
};

export default PacedDetailModuleItem;
